import {
    ChangeDetectionStrategy,
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    inject,
    model,
    ModelSignal,
    Signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, PRIMARY_OUTLET, Router, RouterLink } from '@angular/router';
import { filter, map } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { Country } from '@shs-digi-care-monorepo/interfaces';
import '@shui/core/sh-popover';
import { OpenRequestTab, OpenRequestTabsService } from '../../services/open-request-tabs.service';
import { EmeaFeature, EmeaFeatureService } from '../../services/emea-feature.service';
import { SelectedCountryService } from '../../services/selected-country.service';

/** Index used when no navigation item matches the current route. */
const NO_ACTIVE_ITEM = -1;

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styles: `
        .country-indicator-enter {
            animation: country-indicator-enter 200ms ease-in-out;
        }

        @keyframes country-indicator-enter {
            from {
                opacity: 0;
                transform: scale(0.6);
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink, TranslateModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    host: {
        '[style.flex]': '"0 0 auto"',
        '[style.width]': 'collapsed() ? collapsedWidth : expandedWidth',
        '[style.transition]': '"width 200ms ease-in-out"'
    }
})
export class SidebarComponent {
    private readonly router = inject(Router);
    private readonly openRequestTabs = inject(OpenRequestTabsService);
    private readonly countryScope = inject(SelectedCountryService);
    protected readonly features = inject(EmeaFeatureService);

    protected readonly collapsedWidth = '4.5rem';
    protected readonly expandedWidth = '300px';

    protected readonly tabs: Signal<OpenRequestTab[]> = this.openRequestTabs.tabs;
    protected readonly activeTabId: Signal<string | null> = this.openRequestTabs.activeId;
    protected readonly canAccessAdminArea: Signal<boolean> = this.features.isEnabled(EmeaFeature.ADMIN_AREA);

    public readonly collapsed: ModelSignal<boolean> = model(true);
    protected readonly EmeaFeature = EmeaFeature;

    protected readonly countries: Signal<Country[]> = this.countryScope.countries;
    protected readonly selectedCountry: Signal<Country | null> = this.countryScope.selected;
    /** While the country switch is visible it hosts the collapse toggle instead of the sidebar. */
    protected readonly showCollapser: Signal<boolean> = computed(
        () => !this.countryScope.canSwitch() || this.collapsed()
    );
    protected readonly showCountryIndicator: Signal<boolean> = computed(
        () => this.countryScope.canSwitch() && this.collapsed()
    );
    protected readonly countryLabelKey: Signal<string> = computed(() => {
        const country = this.selectedCountry();
        return country ? `countries.${country.toLowerCase()}` : 'common.general.global';
    });

    private readonly url: Signal<string> = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => event.urlAfterRedirects)
        ),
        { initialValue: this.router.url }
    );

    /**
     * `sh-vertical-nav` owns the active state of its items and resets it whenever
     * its slot changes, so the highlight has to be driven through `active-index`
     * instead of per-item `routerLinkActive` bindings.
     */
    protected readonly activeIndex: Signal<number> = computed(() => {
        const tree = this.router.parseUrl(this.url());
        const segments = tree.root.children[PRIMARY_OUTLET]?.segments.map((segment) => segment.path) ?? [];
        const tabs = this.collapsed() ? [] : this.tabs();

        const homeIndex = 0;
        const requestsIndex = 1;
        const firstTabIndex = requestsIndex + 1;
        const createRequestIndex = firstTabIndex + tabs.length;
        const hasPurchaseOrder = this.features.isActive(EmeaFeature.PURCHASE_ORDER);
        const purchaseOrderIndex = createRequestIndex + 1;
        const adminIndex = createRequestIndex + (hasPurchaseOrder ? 2 : 1);

        if (segments.length === 0) {
            return homeIndex;
        }
        if (segments[0] === 'requests') {
            const tabIndex = tabs.findIndex((tab) => tab.id === tree.queryParams['tab']);
            return tabIndex >= 0 ? firstTabIndex + tabIndex : requestsIndex;
        }
        if (segments[0] === 'request' && segments.length === 1) {
            if (tree.queryParams['type'] === 'PURCHASE_ORDER') {
                return hasPurchaseOrder ? purchaseOrderIndex : NO_ACTIVE_ITEM;
            }
            return createRequestIndex;
        }
        if (segments[0] === 'admin' && this.canAccessAdminArea()) {
            return adminIndex;
        }
        return NO_ACTIVE_ITEM;
    });

    /** Updates the collapsed state whenever the user toggles the sidebar. */
    protected onCollapsedChanged(event: Event): void {
        this.collapsed.set((event as CustomEvent<boolean>).detail);
    }

    /** Scopes the application to one country, or to all of them with `null`. */
    protected selectCountry(country: Country | null): void {
        this.countryScope.select(country);
    }

    /** Activates a previously opened request tab and navigates to the request overview. */
    protected openTab(id: string): void {
        this.openRequestTabs.setActive(id);
        this.router.navigate(['/requests'], { queryParams: { tab: id } });
    }

    /** Closes an opened request tab without navigating, asking first when it holds unsaved changes. */
    protected closeTab(id: string, event: Event): void {
        event.stopPropagation();
        this.openRequestTabs.closeWithConfirmation([id]);
    }
}
